import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import { validatePassword } from "../../utils/utilFunctions";

type SetPasswordDialogProps = {
	open: boolean;
	onClose: () => void;
	userId: string;
	userName?: string;
};

export const SetPasswordDialog: FC<SetPasswordDialogProps> = ({ open, onClose, userId, userName }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const { mutateAsync: setPassword, isPending } = useMutation({
		mutationFn: async () => {
			const validation = validatePassword(newPassword);
			if (!validation.is_valid) {
				const message = validation.errors.map((error) => t(error)).join("\n");
				throw new Error(message);
			}
			if (newPassword !== confirmPassword) {
				throw new Error(t("passwords.do.not.match"));
			}
			await backendService.supportSetPassword({ userId, newPassword });
		},
		onSuccess: () => {
			track("support_set_password_success", { user_id: userId });
			showSnackbar(t("set.password.success"), "success");
			handleClose();
		},
		onError: (error: Error) => {
			trackError("support_set_password_error", { user_id: userId, error_message: error.message });
			showSnackbar(error.message, "error");
		},
	});

	const handleClose = useCallback(() => {
		setNewPassword("");
		setConfirmPassword("");
		setShowNewPassword(false);
		setShowConfirmPassword(false);
		onClose();
	}, [onClose]);

	const handleSubmit = useCallback(async () => {
		track("support_set_password_start", { user_id: userId });
		await setPassword();
	}, [setPassword, track, userId]);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth data-testid="set-password-dialog">
			<DialogTitle>
				{t("set.password")}
				{userName ? ` - ${userName}` : ""}
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
				<TextField
					type={showNewPassword ? "text" : "password"}
					value={newPassword}
					onChange={(e) => setNewPassword(e.target.value)}
					label={t("password")}
					variant="outlined"
					size="small"
					fullWidth
					data-testid="set-password-new-password-input"
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowNewPassword((s) => !s)} edge="end" data-testid="set-password-toggle-new-visibility-button">
										{showNewPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>
				<TextField
					type={showConfirmPassword ? "text" : "password"}
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					label={t("password.confirmation")}
					variant="outlined"
					size="small"
					fullWidth
					data-testid="set-password-confirm-password-input"
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowConfirmPassword((s) => !s)} edge="end" data-testid="set-password-toggle-confirm-visibility-button">
										{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} data-testid="set-password-cancel">
					{t("cancel")}
				</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={isPending || !newPassword || !confirmPassword} data-testid="set-password-confirm">
					{t("set.password")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
